import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { supabase } from '../lib/supabase'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [password, setPassword]   = useState('')
  const [confirm, setConfirm]     = useState('')
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState('')
  const [success, setSuccess]     = useState(false)
  const [validLink, setValidLink] = useState(false)
  const [checking, setChecking]   = useState(true)

  useEffect(() => {
    // Supabase puts the recovery token in the URL hash when the user
    // clicks the email link. The SDK picks it up automatically via
    // onAuthStateChange — we just need to confirm a session exists.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setValidLink(true)
        setChecking(false)
      } else if (session) {
        // Already had a session, still allow reset
        setValidLink(true)
        setChecking(false)
      }
    })

    // Fallback: check for existing session after short delay
    setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) setValidLink(true)
      setChecking(false)
    }, 1200)

    return () => subscription.unsubscribe()
  }, [])

  async function handleReset(e) {
    e.preventDefault()
    setError('')

    if (password.length < 6) {
      setError('Password must be at least 6 characters.'); return
    }
    if (password !== confirm) {
      setError('Passwords do not match.'); return
    }

    setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    setLoading(false)

    if (error) {
      setError(error.message)
    } else {
      setSuccess(true)
      setTimeout(() => navigate('/'), 3000)
    }
  }

  // Loading state
  if (checking) return (
    <div>
      <Navbar />
      <div style={{ textAlign: 'center', padding: '100px 20px', color: 'var(--gray-400)' }}>
        Verifying reset link…
      </div>
    </div>
  )

  // Expired or invalid link
  if (!validLink) return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 440, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10 }}>Link expired or invalid</h2>
        <p style={{ color: 'var(--gray-500)', lineHeight: 1.6, marginBottom: 24 }}>
          Password reset links expire after 1 hour. Request a new one from the login screen.
        </p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Back to Home
        </button>
      </div>
    </div>
  )

  // Success state
  if (success) return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 440, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 10 }}>Password updated!</h2>
        <p style={{ color: 'var(--gray-500)', marginBottom: 8 }}>
          Your password has been changed successfully.
        </p>
        <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>
          Redirecting you home in a moment…
        </p>
      </div>
    </div>
  )

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 420, margin: '60px auto', padding: '0 20px' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/logo.png" alt="Wind Sports Directory" style={{ height: 56, width: 'auto', margin: '0 auto 16px', display: 'block' }} />
          <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Set a new password</h2>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Choose something secure — at least 6 characters.</p>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleReset}
          style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 14, padding: 28 }}>
          <div className="field">
            <label>New password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
              autoFocus
            />
          </div>
          <div className="field">
            <label>Confirm new password</label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>

          {/* Password strength indicator */}
          {password.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{
                    flex: 1, height: 3, borderRadius: 2,
                    background: password.length >= i * 3
                      ? password.length >= 12 ? '#059669'
                        : password.length >= 8 ? '#d97706' : '#dc2626'
                      : 'var(--gray-200)',
                    transition: 'background .2s',
                  }} />
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                {password.length < 6 ? 'Too short' : password.length < 8 ? 'Weak' : password.length < 12 ? 'Good' : 'Strong'}
              </div>
            </div>
          )}

          <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }} disabled={loading}>
            {loading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
